if (!customElements.get('product-customizer')) {
  class ProductCustomizer extends HTMLElement {
    constructor() {
      super();

      this.sectionId = this.dataset.sectionId;
      this.productFormId = this.dataset.productFormId;
      this.variantId = this.dataset.variantId;
      this.mainSlideMediaId = Number(this.dataset.mainSlideMediaId);

      this.categoriesContainer = this.querySelector('.product-customizer__categories');
      this.heading = this.querySelector('.product-customizer__heading');
      this.optionsPanels = this.querySelectorAll('.product-customizer__options');
      this.categoryButtons = this.querySelectorAll('.product-customizer__category');
      this.errorsContainer = this.querySelector('.product-customizer__errors');
      this.hiddenInputsContainer = this.querySelector('.product-customizer__hidden-inputs');

      this.mediaData = this.loadMediaData();
      this.gallery = null;
      this.selectedOptions = {};
      this.pendingPreloads = {};
    }

    connectedCallback() {
      this.bindCategoryNavigation();
      this.bindSwatchSelection();
      this.bindSubmission();
    }

    loadMediaData() {
      const script = this.querySelector(`#product-customizer-media-${this.sectionId}`);
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch {
        return [];
      }
    }

    getGallery() {
      if (this.gallery) return this.gallery;
      const productInfo = this.closest('product-info');
      if (productInfo) {
        this.gallery = productInfo.querySelector('swiper-product-gallery');
      }
      return this.gallery;
    }

    findMainSlide() {
      const gallery = this.getGallery();
      if (!gallery) return null;

      if (this.mainSlideMediaId) {
        const slide = gallery.querySelector(`[data-media-id="${this.mainSlideMediaId}"]`);
        if (slide) return slide;
      }

      return gallery.querySelector('.swiper-slide[data-media-id]');
    }

    bindCategoryNavigation() {
      this.categoryButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          this.showOptions(btn.dataset.categoryIndex);
        });
      });

      this.querySelectorAll('.product-customizer__back-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          this.hideOptions();
        });
      });
    }

    showOptions(categoryIndex) {
      this.categoriesContainer.style.display = 'none';
      this.heading.style.display = 'none';
      this.optionsPanels.forEach(panel => {
        panel.classList.toggle(
          'product-customizer__options--active',
          panel.dataset.categoryIndex === categoryIndex
        );
      });
    }

    hideOptions() {
      this.optionsPanels.forEach(panel => {
        panel.classList.remove('product-customizer__options--active');
      });
      this.categoriesContainer.style.display = '';
      this.heading.style.display = '';
    }

    bindSwatchSelection() {
      this.querySelectorAll('.product-customizer__swatch').forEach(label => {
        label.addEventListener('click', (e) => {
          e.preventDefault();
          const input = label.querySelector('.product-customizer__swatch-input');
          if (!input || input.checked) return;
          input.checked = true;
          input.focus({ preventScroll: true });
          this.onSwatchSelect(input);
        });
      });
    }

    onSwatchSelect(input) {
      const optionName = input.dataset.optionName;
      const swatchName = input.dataset.swatchName;
      const displayValue = input.value;
      const swatchImg = input.closest('.product-customizer__swatch')
        ?.querySelector('.product-customizer__swatch-img');
      const panel = input.closest('.product-customizer__options');
      const optionIndex = panel ? parseInt(panel.dataset.categoryIndex, 10) : 0;

      this.selectedOptions[optionName] = displayValue;
      this.updateCategoryPreview(input, swatchImg, displayValue);
      this.applyImageOverlay(optionName, swatchName, optionIndex);
      this.clearErrors();
    }

    updateCategoryPreview(input, swatchImg, displayValue) {
      const panel = input.closest('.product-customizer__options');
      if (!panel) return;

      const categoryBtn = this.querySelector(
        `.product-customizer__category[data-category-index="${CSS.escape(panel.dataset.categoryIndex)}"]`
      );
      if (!categoryBtn) return;

      const previewImg = categoryBtn.querySelector('.product-customizer__category-img');
      const label = categoryBtn.querySelector('.product-customizer__category-label');

      if (previewImg && swatchImg) {
        previewImg.src = swatchImg.src;
      }
      if (label) {
        label.textContent = displayValue;
      }
    }

    applyImageOverlay(optionName, swatchName, optionIndex) {
      const matchingMedia = this.mediaData.find(m => m.alt === swatchName);
      if (!matchingMedia) {
        console.warn('[customizer] No media found for alt:', swatchName, 'Available alts:', this.mediaData.map(m => m.alt));
        return;
      }

      const mainSlide = this.findMainSlide();
      if (!mainSlide) {
        console.warn('[customizer] No main slide found. mainSlideMediaId:', this.mainSlideMediaId);
        return;
      }

      const priorPreloader = this.pendingPreloads[optionName];
      if (priorPreloader) {
        priorPreloader.onload = null;
        priorPreloader.onerror = null;
      }

      const preloader = new Image();
      this.pendingPreloads[optionName] = preloader;

      preloader.onload = () => {
        if (this.pendingPreloads[optionName] !== preloader) return;
        delete this.pendingPreloads[optionName];

        const existingOverlay = mainSlide.querySelector(
          `.product-customizer-overlay-img[data-customizer-option="${CSS.escape(optionName)}"]`
        );

        const img = document.createElement('img');
        img.className = 'product-customizer-overlay-img';
        img.dataset.customizerOption = optionName;
        img.alt = swatchName;
        img.src = matchingMedia.src;
        img.style.zIndex = 2 + (optionIndex || 0);

        const mediaWrapper = mainSlide.querySelector('.media') || mainSlide;
        if (existingOverlay) {
          existingOverlay.replaceWith(img);
        } else {
          mediaWrapper.appendChild(img);
        }

        this.updateLightboxData(mainSlide);
      };

      preloader.onerror = () => {
        if (this.pendingPreloads[optionName] !== preloader) return;
        delete this.pendingPreloads[optionName];

        const existingOverlay = mainSlide.querySelector(
          `.product-customizer-overlay-img[data-customizer-option="${CSS.escape(optionName)}"]`
        );
        if (existingOverlay) existingOverlay.remove();

        this.updateLightboxData(mainSlide);
        this.showError(`Couldn't load preview for ${swatchName}. Please try again.`);
      };

      preloader.src = matchingMedia.src;

      this.navigateToMainSlide();
    }

    updateLightboxData(slide) {
      const galleryToggle = slide.querySelector('a.product__gallery-toggle');
      if (!galleryToggle) return;

      const baseImg = slide.querySelector('.media img:not(.product-customizer-overlay-img)');
      if (!baseImg) return;

      const baseSrc = galleryToggle.dataset.pswpSrc || baseImg.currentSrc || baseImg.src;
      const width = galleryToggle.dataset.pswpWidth || baseImg.naturalWidth || 1200;
      const height = galleryToggle.dataset.pswpHeight || baseImg.naturalHeight || 1200;

      const overlays = slide.querySelectorAll('.product-customizer-overlay-img');
      if (overlays.length === 0) return;

      let overlayHtml = '';
      overlays.forEach(overlay => {
        overlayHtml += `<img src="${overlay.src}" style="position:absolute;inset:0;width:100%;height:100%;object-fit:contain;pointer-events:none;">`;
      });

      const compositeHtml = `<div style="position:relative;width:${width}px;height:${height}px;max-width:100vw;max-height:100vh;margin:auto;">` +
        `<img src="${baseSrc}" style="width:100%;height:100%;object-fit:contain;">` +
        overlayHtml +
        `</div>`;

      galleryToggle.removeAttribute('data-pswp-image');
      galleryToggle.removeAttribute('data-pswp-src');
      galleryToggle.removeAttribute('data-pswp-srcset');
      galleryToggle.dataset.pswpType = 'html';
      galleryToggle.dataset.pswpHtml = compositeHtml;
    }

    navigateToMainSlide() {
      const gallery = this.getGallery();
      if (!gallery) return;

      if (this.mainSlideMediaId) {
        gallery.setActiveMedia(this.mainSlideMediaId);
        return;
      }

      if (gallery.instance && typeof gallery.instance.slideTo === 'function') {
        gallery.instance.slideTo(0);
      }
    }

    bindSubmission() {
      const form = document.getElementById(this.productFormId);
      if (form) {
        form.addEventListener('submit', (e) => {
          if (!this.validate()) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return;
          }
          this.appendPropertiesToForm(form);
        });
        return;
      }

      const productInfo = this.closest('product-info');
      if (!productInfo) return;

      const submitBtn = productInfo.querySelector('[id^="ProductSubmitButton-"]');
      if (!submitBtn) return;

      submitBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();

        if (!this.validate()) return;
        this.addToCart(submitBtn);
      });
    }

    appendPropertiesToForm(form) {
      Object.entries(this.selectedOptions).forEach(([name, value]) => {
        const existing = form.querySelector(`input[name="properties[${CSS.escape(name)}]"]`);
        if (existing) {
          existing.value = value;
          return;
        }
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${name}]`;
        input.value = value;
        form.appendChild(input);
      });
    }

    addToCart(submitBtn) {
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('loading');
      const spinner = submitBtn.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');

      const formData = {
        id: this.variantId,
        quantity: 1,
        properties: { ...this.selectedOptions }
      };

      fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(formData)
      })
        .then(res => res.json())
        .then(response => {
          if (response.status) {
            this.showError(response.description || response.message);
            return;
          }

          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            fetch(window.location.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
              .then(() => {
                document.documentElement.dispatchEvent(
                  new CustomEvent('cart:refresh', { bubbles: true })
                );
              });
            cartDrawer.classList.add('active');
          } else {
            window.location.href = window.routes?.cart_url || '/cart';
          }
        })
        .catch(() => {
          this.showError('Something went wrong. Please try again.');
        })
        .finally(() => {
          submitBtn.removeAttribute('aria-disabled');
          submitBtn.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        });
    }

    showError(message) {
      this.clearErrors();
      const errorEl = document.createElement('p');
      errorEl.className = 'product-customizer__error';
      errorEl.textContent = message;
      this.errorsContainer.appendChild(errorEl);
    }

    validate() {
      this.clearErrors();
      let valid = true;

      this.optionsPanels.forEach(panel => {
        const optionName = panel.dataset.optionName;
        const checkedInput = panel.querySelector('.product-customizer__swatch-input:checked');

        if (!checkedInput) {
          valid = false;
          const errorEl = document.createElement('p');
          errorEl.className = 'product-customizer__error';
          errorEl.textContent = `Please select a ${optionName}`;
          this.errorsContainer.appendChild(errorEl);
        }
      });

      return valid;
    }

    clearErrors() {
      this.errorsContainer.innerHTML = '';
    }
  }

  customElements.define('product-customizer', ProductCustomizer);
}
